import React, { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle } from 'lucide-react';
import { api } from '../features/auth/authStore';
import toast from 'react-hot-toast';

interface SessionWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExtend: () => void;
  onLogout: () => void;
  timeLeft: number; // in minutes
}

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  onClose,
  onExtend,
  onLogout,
  timeLeft
}) => {
  const [countdown, setCountdown] = useState(timeLeft);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCountdown(timeLeft);
      
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            onLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [isOpen, timeLeft, onLogout]);

  const handleExtendSession = async () => {
    try {
      setExtending(true);
      await api.post('/session/extend');
      toast.success('Session extended successfully');
      onExtend();
      onClose();
    } catch (error) {
      console.error('Failed to extend session:', error);
      toast.error('Failed to extend session');
    } finally {
      setExtending(false);
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 1) return 'less than 1 minute';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 1 && mins === 0) return '1 hour';
    if (mins === 0) return `${hours} hours`;
    return `${hours}h ${mins}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Session Expiring Soon
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-5 h-5 text-amber-500" />
            <p className="text-gray-700">
              Your session will expire in{' '}
              <span className="font-semibold text-amber-600">
                {formatTime(countdown)}
              </span>
            </p>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            To continue working, please extend your session or you will be automatically logged out.
          </p>

          <div className="flex space-x-3">
            <button
              onClick={handleExtendSession}
              disabled={extending}
              className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {extending ? 'Extending...' : 'Extend Session'}
            </button>
            <button
              onClick={onLogout}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionWarningModal;
